import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import WorkoutNumberInput from "./WorkoutNumberInput";
import { FITNESS_WORKOUT_FINISH_LIMITS as LIMITS } from "../../../supabase/functions/_shared/fitnessWorkoutFinishDomain";
import { Check, ChevronRight, Pencil } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import ExerciseDemonstration from "@/components/fitness/ExerciseDemonstration";
import ExerciseCoach from "@/components/fitness/ExerciseCoach";
import FitnessIntervalTraining from "@/components/fitness/FitnessIntervalTraining";
import { fitnessIntervalKind } from "@/lib/fitnessIntervalProtocol";
import type { FitnessInstructorId } from "@/lib/fitnessInstructor";
import type { ExerciseMediaSources } from "@/hooks/useExerciseMedia";

interface WorkoutExecutionProps {
  exerciseName: string;
  exerciseId?: string;
  instructorId?: FitnessInstructorId;
  instructorSelector?: React.ReactNode;
  sessionTimeline?: React.ReactNode;
  setMetadata?: React.ReactNode;
  exerciseActions?: React.ReactNode;
  speechScopeId?: string;
  intervalScopeId?: string;
  timed?: boolean;
  muscleGroup: string;
  currentSet: number;
  totalSets: number;
  weight: number;
  reps: number;
  onSetDone: () => void;
  onEditClick?: () => void;
  onValuesChange?: (values: { weight: number; reps: number }) => void;
  onTimedTargetApply?: (seconds: number) => boolean;
  media?: Partial<ExerciseMediaSources>;
  mediaLoading?: boolean;
  mediaError?: string | null;
  onMediaRetry?: () => void;
  instructions?: string[];
  nextExerciseName?: string;
  paused?: boolean;
  previousPerformance?: React.ReactNode;
}

const WorkoutExecution: React.FC<WorkoutExecutionProps> = ({
  exerciseName, exerciseId, instructorId, instructorSelector, sessionTimeline, setMetadata, exerciseActions, speechScopeId, intervalScopeId, timed = false, muscleGroup, currentSet, totalSets,
  weight, reps, onSetDone, onEditClick, onValuesChange, media, mediaLoading, mediaError,
  onMediaRetry, instructions, nextExerciseName, paused = false, onTimedTargetApply, previousPerformance,
}) => {
  const { t, tf } = useLanguage();
  const rootRef = useRef<HTMLDivElement>(null), footerRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const root = rootRef.current, footer = footerRef.current;
    if (!root || !footer) return;
    let queued: number | null = null;
    const fitStage = () => {
      queued = null;
      if (window.innerWidth >= 1024) { root.style.removeProperty("--fitness-stage-height"); return; }
      const stage = root.querySelector<HTMLElement>("[data-fitness-demonstration-viewport]");
      if (!stage) return;
      const bounds = stage.getBoundingClientRect(), footerBounds = footer.getBoundingClientRect();
      if (bounds.width <= 0 || footerBounds.top <= 0) return;
      // Use document position so scrolling never grows/shrinks the demonstration.
      const available = Math.max(180, Math.min(440, footerBounds.top - (bounds.top + window.scrollY) - 12));
      const next = available + "px";
      if (root.style.getPropertyValue("--fitness-stage-height") !== next) root.style.setProperty("--fitness-stage-height", next);
    };
    const queueFit = () => { if (queued === null) queued = requestAnimationFrame(fitStage); };
    fitStage();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(queueFit);
    observer?.observe(root); observer?.observe(footer);
    window.addEventListener("resize", queueFit); window.visualViewport?.addEventListener("resize", queueFit);
    return () => {
      if (queued !== null) cancelAnimationFrame(queued);
      observer?.disconnect();
      window.removeEventListener("resize", queueFit); window.visualViewport?.removeEventListener("resize", queueFit);
    };
  }, [exerciseId, exerciseName, intervalScopeId]);
  const [invalidFields, setInvalidFields] = useState<Set<string>>(() => new Set());
  useEffect(() => { setInvalidFields(new Set()); }, [exerciseId, exerciseName, currentSet]);
  const markValidity = (key: string, valid: boolean) => setInvalidFields(current => {
    if (valid === !current.has(key)) return current;
    const next = new Set(current); if (valid) next.delete(key); else next.add(key); return next;
  });
  const intervalKind = timed ? fitnessIntervalKind(exerciseId ?? exerciseName) : null;
  const handleDone = () => {
    if (paused || invalidFields.size) return;
    try { navigator.vibrate?.(50); } catch { /* Haptics are optional. */ }
    onSetDone();
  };
  const seriesLabel = tf("fitness.workout.seriesOf", { current: String(currentSet), total: String(totalSets) });
  const target = <span>{(!timed || weight !== 0) && <>{weight}{t("fitness.weightUnit")} &times; </>}{reps} {t(timed ? "fitness.ux.seconds" : "fitness.workout.reps")}</span>;

  return (
    <div ref={rootRef} className="fitness-execution mx-auto flex w-full max-w-6xl flex-1 flex-col gap-4 px-4 pb-40 lg:pb-8">
      <header className="pt-2">
        <p className="mb-1 text-xs font-display font-medium uppercase tracking-wider text-primary">{muscleGroup}</p>
        <h2 className="font-display text-2xl font-bold leading-tight text-foreground sm:text-3xl">{exerciseName}</h2>
      </header>
      <div className="grid min-w-0 gap-5 lg:grid-cols-[minmax(0,1.5fr)_minmax(320px,1fr)] lg:items-start">
      <div className="min-w-0 space-y-4 lg:sticky lg:top-4">
      {instructorSelector}
      {intervalKind ? <FitnessIntervalTraining kind={intervalKind}
        scopeId={JSON.stringify([intervalScopeId ?? speechScopeId ?? null, exerciseId ?? exerciseName, currentSet])}
        instructorId={instructorId} currentSet={currentSet} targetSeconds={reps} paused={paused} onTargetApply={onTimedTargetApply} /> : <>
        <ExerciseDemonstration exerciseId={exerciseId} exerciseName={exerciseName} instructorId={instructorId} variant="stage"
          media={media} loading={mediaLoading} error={mediaError} onRetry={onMediaRetry}
          paused={paused} instructions={instructions} />
      </>}
      </div>
      <div className="min-w-0 space-y-4">
      <div className="rounded-2xl border border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <p className="font-display text-base font-semibold text-foreground">{seriesLabel}</p>
          <progress max={Math.max(totalSets, 1)} value={Math.max(currentSet - 1, 0)} aria-label={seriesLabel} className="h-2 w-24 accent-primary" />
        </div>
        {onEditClick ? (
          <button type="button" onClick={onEditClick} disabled={paused}
            className="flex min-h-12 w-full items-center justify-between gap-3 rounded-xl border border-border bg-background px-4 py-3 text-lg font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40"
            aria-label={t("fitness.workout.editSet")}>
            {target}<Pencil size={18} aria-hidden="true" className="text-primary" />
          </button>
        ) : <p className="py-2 text-lg font-semibold text-foreground">{target}</p>}
        {setMetadata}
        {previousPerformance}
        {onValuesChange && <div key={exerciseName + ':' + currentSet} className="mt-4 grid grid-cols-2 gap-3">
          <WorkoutNumberInput label={t("fitness.workout.weightLabel")} value={weight} min={0} max={LIMITS.weight} disabled={paused}
            onChange={next => onValuesChange({ weight: next, reps })} onValidityChange={valid => markValidity("weight", valid)} />
          <WorkoutNumberInput label={t(timed ? "fitness.ux.seconds" : "fitness.workout.reps")} value={reps} min={1} max={LIMITS.reps} integer disabled={paused}
            onChange={next => onValuesChange({ weight, reps: next })} onValidityChange={valid => markValidity("reps", valid)} />
        </div>}
        <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{t("fitness.demo.completeHint")}</p>
      </div>
      <div ref={footerRef} className="fixed inset-x-3 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-40 mx-auto max-w-md rounded-2xl border border-border bg-background/95 p-3 shadow-lg lg:static lg:mx-0 lg:max-w-none lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none">
        <div className="mb-2 flex min-h-9 items-center justify-between gap-3 lg:hidden">
          <p className="shrink-0 text-sm font-semibold">{seriesLabel}</p>
          {onEditClick ? <button type="button" onClick={onEditClick} disabled={paused}
            aria-label={t("fitness.workout.editSetTitle")}
            className="flex min-h-11 min-w-0 items-center justify-end gap-2 rounded-xl px-2 text-right text-sm font-semibold tabular-nums text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40">
            {target}<Pencil size={15} aria-hidden="true" />
          </button> : <p className="text-sm font-semibold tabular-nums">{target}</p>}
        </div>
        <button type="button" onClick={handleDone} disabled={paused || invalidFields.size > 0}
          className="flex min-h-14 w-full items-center justify-center gap-3 rounded-xl bg-primary px-5 py-3 font-display text-xl font-bold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-40 lg:min-h-16 lg:rounded-2xl lg:py-4">
          <Check size={24} aria-hidden="true" /><span>{t("fitness.workout.doneBtn")}</span>
        </button>
      </div>
      {nextExerciseName && <div className="flex items-center justify-between gap-3 border-t border-border pt-4">
        <div className="min-w-0"><p className="text-xs text-muted-foreground">{t("fitness.demo.upNext")}</p><p className="mt-1 truncate text-sm font-medium text-foreground">{nextExerciseName}</p></div>
        <ChevronRight size={18} aria-hidden="true" className="shrink-0 text-muted-foreground" />
      </div>}
      {!intervalKind && <ExerciseCoach speechScopeId={speechScopeId} exerciseId={exerciseId} exerciseName={exerciseName} currentSet={currentSet} paused={paused} />}
      {exerciseActions}
      {sessionTimeline}
      </div>
      </div>
    </div>
  );
};

export default React.memo(WorkoutExecution);
